<template>
  <AccountCard :title="$t('pages.account.settings.sections.new_email.title')" title-tag="h2">
    <div class="my-3">
      <p>{{ props.profileDetails.email }}</p>
    </div>
    <hr class="my-6" />
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="min-w-[15rem] flex-1">
        <h4 class="font-semibold">
          {{ $t("pages.account.settings.sections.new_email.subtitle") }}
        </h4>

        <div class="my-3">
          <p>
            {{
              $t("pages.account.settings.sections.new_email.unconfirmed_change") +
              " " +
              props.profileDetails.updated_email
            }}
          </p>
        </div>

        <CommonAlert v-if="alert.type" :type="alert.type" :content="alert.message" class="mb-6 mt-4" />

        <div class="mt-8 inline-flex">
          <div class="flex min-w-max flex-col gap-4">
            <BaseButton
              type="submit"
              class="w-full text-[1rem]"
              :loading="pending"
              size="medium"
              @buttonClicked="resendConfirmationEmail"
            >
              {{ $t("pages.account.buttons.resend_confirmation_email") }}
            </BaseButton>

            <BaseButton
              type="submit"
              class="w-full text-[1rem]"
              :loading="pending"
              size="medium"
              @buttonClicked="cancelEmailChange"
              variant="outlined"
            >
              {{ $t("pages.account.buttons.cancel_email_change") }}
            </BaseButton>
          </div>
        </div>
      </div>
      <div class="flex-grow basis-60 rounded-md bg-[#D8D8D84D] p-4 text-sm md:flex-grow-0">
        {{ $t("pages.account.settings.sections.email.confirmation_note") }}
      </div>
    </div>
  </AccountCard>
</template>

<script setup lang="ts">
import { useProfileStore } from "@/stores/profile";
import * as yup from "yup";
import type { User } from "~/entities/User";
import AccountCard from "../components/account-card.vue";

const { t } = useI18n();

interface AccountSettingsPersonalDetailsProps {
  profileDetails: User.ProfileDetails;
}
const props = defineProps<AccountSettingsPersonalDetailsProps>();

const Schema = yup.object({
  type: yup.string().optional().default("email"),
  email: yup.string().email().required(),
  email_confirmation: yup
    .string()
    .oneOf([yup.ref("email")])
    .required(),
  password: yup.string().required(),
});
type Schema = yup.InferType<typeof Schema>;

const pending = ref(false);
const alert = reactive<{
  message: string | null;
  type: "success" | "error" | null;
}>({
  message: null,
  type: null,
});

const cancelEmailChange = async () => {
  try {
    pending.value = true;
    const { data: response } = await useCancelEmailUpdate({
      type: "cancel-email-update",
    });
    if (response.value) {
      console.log("API response:", response.value.data);

      pending.value = false;
      alert.message = t("pages.account.settings.sections.new_email.cancelled");
      alert.type = "success";

      // Fetch the updated profile details to update store
      const profileStore = useProfileStore();
      await profileStore.fetchProfile();
    }
  } catch (error: any) {
    pending.value = false;
    alert.message = error?.data?.message || t("pages.account.settings.sections.new_email.error");
    alert.type = "error";
  }
};

const resendConfirmationEmail = async () => {
  try {
    pending.value = true;
    const profileStore = useProfileStore();

    const { data: response } = await useResendConfirmationEmail(
      profileStore.authUser.userId,
      profileStore.authUser.verificationToken
    );
    if (response.value) {
      console.log("API response:", response.value.data);

      pending.value = false;
      alert.message = t("pages.account.settings.sections.new_email.resent");
      alert.type = "success";
    }
  } catch (error: any) {
    pending.value = false;
    alert.message = error?.data?.message || t("pages.account.settings.sections.new_email.error");
    alert.type = "error";
  }
};
</script>
